import type { UA, WebSocketInterface } from 'jssip';

import { logger } from '@/src/logging/logger';

import { AudioPreferenceError } from './audio-device-preferences';

import type { BrowserOriginatePreparedCall } from './browser-originated-call-contract';
import type { VoiceBrowserSession } from './types';

/**
 * Browser SIP transport. It owns registration and media but deliberately does
 * not decide whether an incoming INVITE should be answered. That decision is
 * made by the authenticated runtime after correlating the dialog with signed
 * application state or after an explicit user action.
 */

export type SipSoftphoneStatus =
  | 'idle'
  | 'requesting-mic'
  | 'registering'
  | 'registered'
  | 'dialing'
  | 'outgoing_ringing'
  | 'incoming'
  | 'answering'
  | 'connected'
  | 'rejected'
  | 'failed'
  | 'ended';

export type SipDialogState =
  | 'dialing'
  | 'ringing'
  | 'answering'
  | 'connected'
  | 'rejected'
  | 'ended'
  | 'failed';

export type SipOutgoingCallState = Extract<SipDialogState, 'dialing' | 'ringing' | 'connected' | 'failed' | 'ended'>;

export interface SipDialogSnapshot {
  dialogId: string;
  direction: 'incoming' | 'outgoing';
  state: SipDialogState;
  /** Opaque lookup key only. Never caller/CRM authority. */
  sipCorrelationId: string | null;
}

export interface SipSoftphoneEvent {
  status: SipSoftphoneStatus;
  message?: string;
  dialog?: SipDialogSnapshot;
  failureCategory?: SipFailureCategory;
  mediaState?: SipMediaState;
  mediaStage?: SipMediaStage;
  /** Bounded diagnostic latency from SIP confirmation, or track observation when earlier. */
  mediaElapsedMs?: number;
  /** Present for registration lifecycle events so credential rotation is unambiguous. */
  registrationSessionId?: string;
  /** Retryable failures retain the UA; terminal failures require new credentials. */
  registrationFailure?: 'retryable' | 'terminal';
  /** The in-place retry bound was reached and the runtime should replace credentials. */
  registrationRecoveryExhausted?: boolean;
}

export type SipFailureCategory =
  | 'media'
  | 'registration'
  | 'signaling'
  | 'other';

export type SipMediaState =
  | 'idle'
  | 'connecting'
  | 'ready'
  | 'delayed'
  | 'blocked';

export type SipMediaStage =
  | 'signaling-confirmed'
  | 'track-received'
  | 'playback-retry'
  | 'playback-ready'
  | 'playback-timeout'
  | 'playback-blocked';

export type SipUaAuthority = 'active' | 'cutover_predecessor' | 'cutover_successor' | 'revoked';
export type SipUaLifecycle = 'starting' | 'registered' | 'retiring' | 'degraded' | 'stopped';
export type SipSocketPayloadKind = 'text' | 'binary';
/** Bounded, safe classification for JsSIP request rejections before newRTCSession. */
export type SipPreCallbackRejectionStatus = 400 | 404 | 405 | 415 | 416 | 481 | 482 | 488 | 500;

export type SipSoftphoneDiagnosticEvent =
  | { kind: 'offer_received'; sipCorrelationId: string | null; dialogDirection: 'incoming' | 'outgoing' }
  | { kind: 'offer_rejected_authority'; dialogDirection: 'incoming' | 'outgoing' }
  | { kind: 'correlation_present' | 'correlation_missing' | 'correlation_malformed'; sipCorrelationId?: string; dialogDirection: 'incoming' | 'outgoing' }
  | { kind: 'auto_answer_started' | 'answer_invoked' | 'auto_answer_failed'; sipCorrelationId: string | null }
  | { kind: 'remote_playback_blocked'; sipCorrelationId: string | null }
  | { kind: 'session_confirmed' | 'session_failed' | 'session_ended'; sipCorrelationId: string | null }
  | { kind: 'registration_ready' | 'registration_disconnected'; registrationState: 'registered' | 'disconnected' }
  | {
      kind: 'transport_message_received' | 'transport_invite_observed' | 'invite_callback_invoked';
      generation: number;
      authority: SipUaAuthority;
      lifecycle: SipUaLifecycle;
      callbackBound: boolean;
      textOrBinary: SipSocketPayloadKind;
      byteLengthBucket: number;
    }
  | {
      kind: 'invite_rejected_before_callback';
      generation: number;
      authority: SipUaAuthority;
      lifecycle: SipUaLifecycle;
      callbackBound: boolean;
      responseStatus: SipPreCallbackRejectionStatus;
    }
  | {
      kind: 'invite_callback_timeout';
      generation: number;
      authority: SipUaAuthority;
      lifecycle: SipUaLifecycle;
      callbackBound: boolean;
      elapsedMs: number;
    };

export type SipAudioDevice = {
  deviceId: string;
  kind: 'audioinput' | 'audiooutput';
  label: string;
};

type UaConfiguration = ConstructorParameters<typeof UA>[0];

/** A short application-level bound between raw WSS delivery and JsSIP's public
 * newRTCSession callback. This is deliberately not configurable at runtime. */
export const INVITE_CALLBACK_WATCHDOG_MS = 1_500;
const CREDENTIAL_CUTOVER_RETIREMENT_TIMEOUT_MS = 5_000;
const REGISTRATION_RECOVERY_MAX_ATTEMPTS = 4;
const REGISTRATION_RECOVERY_BASE_DELAY_MS = 1_000;

type InstrumentedSocketDelegate = WebSocketInterface;

export type SipSocketObservation =
  | {
      kind: 'message_received' | 'invite_observed';
      textOrBinary: SipSocketPayloadKind;
      byteLengthBucket: number;
    }
  | {
      /** Only an allowlisted numeric status is exposed; no SIP payload is retained. */
      kind: 'response_sent';
      responseStatus: SipPreCallbackRejectionStatus;
    };

export type InstrumentedSipSocketObserver = (event: SipSocketObservation) => void;

const noopSocketCallback = (): void => undefined;

function isArrayBuffer(value: unknown): value is ArrayBuffer {
  return value instanceof ArrayBuffer || Object.prototype.toString.call(value) === '[object ArrayBuffer]';
}

function byteLengthBucket(value: unknown): number {
  const length = typeof value === 'string'
    ? new TextEncoder().encode(value).byteLength
    : isArrayBuffer(value)
      ? value.byteLength
      : ArrayBuffer.isView(value)
        ? value.byteLength
        : typeof Blob !== 'undefined' && value instanceof Blob
          ? value.size
          : 0;
  if (length <= 0) return 0;
  if (length <= 256) return 256;
  if (length <= 1_024) return 1_024;
  if (length <= 4_096) return 4_096;
  if (length <= 16_384) return 16_384;
  return 65_536;
}

function payloadKind(value: unknown): SipSocketPayloadKind {
  return typeof value === 'string' ? 'text' : 'binary';
}

function preCallbackRejectionStatus(value: unknown): SipPreCallbackRejectionStatus | null {
  if (typeof value !== 'string') return null;
  const status = /^SIP\/2\.0[ \t]+(400|404|405|415|416|481|482|488|500)(?:[ \t]|\r?\n|$)/.exec(value)?.[1];
  return status ? Number(status) as SipPreCallbackRejectionStatus : null;
}

function isInvitePayload(value: unknown): boolean {
  let text: string | null = null;
  if (typeof value === 'string') text = value;
  else if (isArrayBuffer(value) || ArrayBuffer.isView(value)) {
    try {
      const bytes = isArrayBuffer(value)
        ? new Uint8Array(value, 0, Math.min(value.byteLength, 32))
        : new Uint8Array(value.buffer, value.byteOffset, Math.min(value.byteLength, 32));
      text = new TextDecoder().decode(bytes);
    } catch {
      logger.warn('sparkone.browser_voice.sip_socket_payload_inspection_failed', {
        payloadKind: 'binary',
      });
      return false;
    }
  }
  return text !== null && /^INVITE[ \t]/.test(text);
}

/**
 * Public JsSIP socket adapter. It preserves JsSIP's socket surface while
 * observing inbound traffic before forwarding it to JsSIP exactly once.
 */
export class InstrumentedSipSocket {
  onconnect: () => void = noopSocketCallback;
  ondisconnect: (error: boolean, code?: number, reason?: string) => void = noopSocketCallback;
  ondata: <T>(data: T) => void = noopSocketCallback;
  private readonly inboundQueue: unknown[] = [];
  private drainingInboundQueue = false;

  constructor(
    private readonly delegate: InstrumentedSocketDelegate,
    private readonly observer: InstrumentedSipSocketObserver,
  ) {
    delegate.onconnect = () => this.onconnect();
    delegate.ondisconnect = (error: boolean, code?: number, reason?: string) =>
      this.ondisconnect(error, code, reason);
    delegate.ondata = <T>(data: T) => this.receive(data);
  }

  get url(): string { return this.delegate.url; }
  get sip_uri(): string { return this.delegate.sip_uri; }
  get via_transport(): string { return this.delegate.via_transport; }
  set via_transport(value: string) { this.delegate.via_transport = value; }

  connect(): void { this.delegate.connect(); }
  disconnect(): void { this.delegate.disconnect(); }
  send(message: string | ArrayBufferLike | Blob | ArrayBufferView): boolean {
    const sent = this.delegate.send(message);
    const responseStatus = sent ? preCallbackRejectionStatus(message) : null;
    if (responseStatus !== null) this.observe({ kind: 'response_sent', responseStatus });
    return sent;
  }
  isConnected(): boolean { return this.delegate.isConnected(); }
  isConnecting(): boolean { return this.delegate.isConnecting(); }

  private receive<T>(data: T): void {
    this.inboundQueue.push(data);
    this.drainInboundQueue();
  }

  private drainInboundQueue(): void {
    if (this.drainingInboundQueue) return;
    while (this.inboundQueue.length > 0) {
      const data = this.inboundQueue.shift();
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        // JsSIP 3.13.8 Transport._onData accepts text and BufferSource payloads,
        // but TextDecoder.decode(Blob) throws before Parser.parseMessage. Convert
        // only the WebSocket container to its byte-identical ArrayBuffer form;
        // do not inspect, parse, normalize, or reserialize SIP content. Pause the
        // FIFO while conversion is pending so later SIP frames cannot overtake it.
        this.drainingInboundQueue = true;
        void data.arrayBuffer()
          .then((bytes) => this.observeAndForward(bytes))
          .catch(() => {
            // A Blob read failure is bounded to this queue position. It must not
            // expose payload-derived details or prevent later messages from being
            // delivered in their original WebSocket order.
            logger.warn('sparkone.browser_voice.sip_socket_blob_read_failed', {
              payloadKind: 'binary',
              byteLengthBucket: byteLengthBucket(data),
            });
          })
          .finally(() => {
            this.drainingInboundQueue = false;
            this.drainInboundQueue();
          });
        return;
      }
      this.observeAndForward(data);
    }
  }

  private observeAndForward<T>(data: T): void {
    const observation: SipSocketObservation = {
      kind: isInvitePayload(data) ? 'invite_observed' : 'message_received',
      textOrBinary: payloadKind(data),
      byteLengthBucket: byteLengthBucket(data),
    };
    this.observe(observation);
    this.ondata(data);
  }

  private observe(observation: SipSocketObservation): void {
    try {
      this.observer(observation);
    } catch {
      // Diagnostics are fail-open. Never interfere with JsSIP transport delivery.
      logger.warn('sparkone.browser_voice.sip_socket_observer_failed', {
        observationKind: observation.kind,
        ...(observation.kind === 'response_sent'
          ? { responseStatus: observation.responseStatus }
          : { payloadKind: observation.textOrBinary, byteLengthBucket: observation.byteLengthBucket }),
      });
    }
  }
}

export function createInstrumentedSipSocket(
  delegate: InstrumentedSocketDelegate,
  observer: InstrumentedSipSocketObserver,
): InstrumentedSipSocket {
  return new InstrumentedSipSocket(delegate, observer);
}

interface SipRtcSession {
  direction: 'incoming' | 'outgoing';
  connection: RTCPeerConnection | null;
  answer(options?: { mediaStream?: MediaStream; pcConfig?: RTCConfiguration }): void;
  terminate(options?: unknown): void;
  on(event: 'progress' | 'confirmed' | 'ended' | 'failed', listener: () => void): void;
  on(
    event: 'peerconnection',
    listener: (data: { peerconnection: RTCPeerConnection }) => void,
  ): void;
}

export interface SipSoftphoneJsSipModule {
  UA: new (configuration: UaConfiguration) => UA;
  WebSocketInterface: new (url: string) => WebSocketInterface;
}

/** Narrow injection seam for deterministic transport integration coverage. */
export interface SipSoftphoneDependencies {
  loadJsSip?: () => Promise<SipSoftphoneJsSipModule>;
}

interface NewRTCSessionData {
  session: SipRtcSession;
  originator: 'local' | 'remote';
  request: { getHeader(name: string): string | undefined };
}

type DialogEntry = {
  snapshot: SipDialogSnapshot;
  rtc: SipRtcSession;
  ownerUa: UA;
  session: VoiceBrowserSession;
  remoteTrackBound: boolean;
  remoteTrackObserved: boolean;
  peerconnection: RTCPeerConnection | null;
  inboundRtpBaseline: number | null;
  inboundRtpBaselineCaptured: boolean;
  inboundRtpBaselinePromise: Promise<void> | null;
  inboundRtpClassified: boolean;
  mediaState: SipMediaState;
  mediaStartedAt: number | null;
  mediaTimeout: ReturnType<typeof setTimeout> | null;
  remoteStream: MediaStream | null;
  mediaAttempt: number;
};

type UaRuntime = {
  generation: number;
  sessionId: string;
  session: VoiceBrowserSession;
  ua: UA;
  socket: InstrumentedSipSocket;
  authority: SipUaAuthority;
  lifecycle: SipUaLifecycle;
  inviteCallbackBound: boolean;
  inviteWatchdog: ReturnType<typeof setTimeout> | null;
  inviteObservedAt: number | null;
  retirementTimeout: ReturnType<typeof setTimeout> | null;
  registrationRecoveryTimeout: ReturnType<typeof setTimeout> | null;
  registrationRecoveryAttempts: number;
};

const correlationPattern = /^svp1\.[A-Za-z0-9_-]{22,128}$/;
const mediaReadinessTimeoutMs = 3_000;

/**
 * A dialog takes exactly two aggregate inbound-RTP samples: one when its
 * remote audio track is observed and one when audio readiness times out.
 * Stats never leave this module; only the allowlisted state is logged.
 */
type InboundRtpState = 'none' | 'advancing' | 'unavailable';

const toIceServers = (session: VoiceBrowserSession): RTCIceServer[] =>
  session.iceServers.map((server) => ({
    urls: server.urls,
    ...(server.username !== undefined ? { username: server.username } : {}),
    ...(server.credential !== undefined ? { credential: server.credential } : {}),
  }));

function readCorrelation(request: NewRTCSessionData['request']): string | null {
  const value = request.getHeader('X-Spark-VP-Correlation')?.trim();
  return value && correlationPattern.test(value) ? value : null;
}

function correlationHeaderState(request: NewRTCSessionData['request']): 'absent' | 'valid' | 'malformed' {
  const value = request.getHeader('X-Spark-VP-Correlation')?.trim();
  if (!value) return 'absent';
  return correlationPattern.test(value) ? 'valid' : 'malformed';
}

function createDialogId(): string {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  return `dialog-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function stopStreamTracks(stream: MediaStream, failureCategory: string): void {
  let tracks: MediaStreamTrack[];
  try {
    tracks = stream.getTracks();
  } catch {
    logger.warn('sparkone.browser_voice.audio_device_operation_failed', { failureCategory });
    return;
  }
  for (const track of tracks) {
    try {
      track.stop();
    } catch {
      logger.warn('sparkone.browser_voice.audio_device_operation_failed', { failureCategory });
    }
  }
}

export class SipSoftphone {
  private jssip: SipSoftphoneJsSipModule | null = null;
  private readonly loadJsSip: () => Promise<SipSoftphoneJsSipModule>;
  private ua: UA | null = null;
  private activeRuntime: UaRuntime | null = null;
  private readonly runtimes = new Map<UA, UaRuntime>();
  private nextRuntimeGeneration = 0;
  private readonly dialogs = new Map<string, DialogEntry>();
  private localStream: MediaStream | null = null;
  private remoteAudio: HTMLAudioElement | null = null;
  // The single audio element can render only one stream at a time. Retain the
  // dialog that last attached its stream so terminating an unrelated ringing
  // or failed dialog cannot silence an established call.
  private remoteAudioDialogId: string | null = null;
  private currentStatus: SipSoftphoneStatus = 'idle';
  private acceptingOffers = false;
  private managedCallerMediaPrepared = false;
  /** Serializes outgoing authorization consumption before a dialog exists. */
  private outgoingInvocationInProgress = false;
  /** Allows cancellation to claim an authorized invocation before JsSIP creates a dialog. */
  private pendingOutgoingInvocation: {
    preparedCallId: string;
    correlationId: string;
    cancelled: boolean;
  } | null = null;
  private pendingCredentialRotation: {
    predecessor: UaRuntime;
    successor: UaRuntime;
    successorSessionId: string;
  } | null = null;
  private muted = false;
  /** Desired input route; configuring it without capture must not prompt. */
  private inputDeviceId = '';
  /** Last successfully applied output route; empty means the browser default. */
  private outputDeviceId = '';
  onChange?: (event: SipSoftphoneEvent) => void;
  onDiagnostic?: (event: SipSoftphoneDiagnosticEvent) => void;

  constructor(
    onChange?: (event: SipSoftphoneEvent) => void,
    onDiagnostic?: (event: SipSoftphoneDiagnosticEvent) => void,
    dependencies?: SipSoftphoneDependencies,
  ) {
    this.onChange = onChange;
    this.onDiagnostic = onDiagnostic;
    this.loadJsSip = dependencies?.loadJsSip ?? (async () =>
      (await import('jssip')) as unknown as SipSoftphoneJsSipModule);
  }

  getStatus(): SipSoftphoneStatus {
    return this.currentStatus;
  }

  getDialogs(): SipDialogSnapshot[] {
    return Array.from(this.dialogs.values(), ({ snapshot }) => ({ ...snapshot }));
  }

  isActive(): boolean {
    return this.ua !== null || this.dialogs.size > 0;
  }

  isMuted(): boolean {
    return this.muted;
  }

  isAcceptingOffers(): boolean {
    return this.acceptingOffers;
  }

  async prepareManagedCallerMedia(): Promise<void> {
    if (!this.ua || !this.acceptingOffers || this.currentStatus !== 'registered') {
      throw new Error('Browser phone is not ready to prepare call media.');
    }
    try {
      const stream = await this.ensureMicrophone();
      if (!stream.getAudioTracks().some((track) => track.readyState === 'live')) {
        throw new Error('Microphone has no live audio track.');
      }
      this.managedCallerMediaPrepared = true;
      this.emit('registered', 'Browser phone media ready.');
    } catch {
      this.releasePreparedManagedCallerMedia();
      this.emit(
        'registered',
        'Browser phone ready; microphone access is unavailable.',
        undefined,
        'media',
      );
      throw new Error('Microphone access was denied or unavailable.');
    }
  }

  releasePreparedManagedCallerMedia(): void {
    if (
      Array.from(this.dialogs.values()).some(({ snapshot }) =>
        ['answering', 'connected'].includes(snapshot.state),
      )
    ) {
      return;
    }
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = null;
    this.managedCallerMediaPrepared = false;
    this.muted = false;
  }

  async getAudioDevices(): Promise<SipAudioDevice[]> {
    if (!navigator.mediaDevices?.enumerateDevices) {
      throw new AudioPreferenceError('AUDIO_PREFERENCES_DEVICE_LIST_UNAVAILABLE');
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices
        .filter((device): device is MediaDeviceInfo & { kind: 'audioinput' | 'audiooutput' } =>
          device.kind === 'audioinput' || device.kind === 'audiooutput')
        .map((device) => ({
          deviceId: device.deviceId,
          kind: device.kind,
          label: device.label || (device.kind === 'audioinput' ? 'Microphone' : 'Speaker'),
        }));
    } catch {
      logger.warn('sparkone.browser_voice.audio_device_operation_failed', {
        failureCategory: 'device_enumeration_failed',
      });
      throw new AudioPreferenceError('AUDIO_PREFERENCES_DEVICE_LIST_UNAVAILABLE');
    }
  }

  supportsAudioOutputSelection(): boolean {
    return Boolean(this.remoteAudio && 'setSinkId' in this.remoteAudio);
  }

  /** Returns only the routes that have become local runtime truth. */
  getSelectedAudioDeviceIds(): { inputDeviceId: string; outputDeviceId: string } {
    return { inputDeviceId: this.inputDeviceId, outputDeviceId: this.outputDeviceId };
  }

  /**
   * Permission-neutral configuration for restore/reconciliation. It never captures
   * media and is forbidden while an active local route would be changed.
   */
  stageInputDevice(deviceId: string): void {
    if (typeof deviceId !== 'string') {
      throw new AudioPreferenceError('AUDIO_PREFERENCES_MICROPHONE_UNAVAILABLE');
    }
    if (this.localStream?.getAudioTracks().some((track) => track.readyState === 'live')) {
      throw new AudioPreferenceError('AUDIO_PREFERENCES_MICROPHONE_SWITCH_FAILED');
    }
    this.inputDeviceId = deviceId;
  }

  /** Clears a staged future microphone route without requesting media. */
  clearStagedInputDevice(): void {
    this.stageInputDevice('');
  }

  /** Clears a staged future speaker route without invoking setSinkId. */
  clearStagedOutputDevice(): void {
    if (this.remoteAudio) {
      throw new AudioPreferenceError('AUDIO_PREFERENCES_SPEAKER_SWITCH_FAILED');
    }
    this.outputDeviceId = '';
  }

  async setInputDevice(deviceId: string): Promise<void> {
    if (typeof deviceId !== 'string') {
      logger.warn('sparkone.browser_voice.audio_device_operation_failed', { failureCategory: 'input_device_invalid' });
      throw new AudioPreferenceError('AUDIO_PREFERENCES_MICROPHONE_UNAVAILABLE');
    }
    if (deviceId === this.inputDeviceId) return;

    if (!this.localStream?.getAudioTracks().some((track) => track.readyState === 'live')) {
      this.stageInputDevice(deviceId);
      return;
    }

    let replacement: MediaStream | null = null;
    try {
      replacement = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        video: false,
      });
      const candidateTrack = replacement.getAudioTracks().find((track) => track.readyState === 'live');
      if (!candidateTrack) {
        logger.warn('sparkone.browser_voice.audio_device_operation_failed', { failureCategory: 'input_device_track_unavailable' });
        throw new AudioPreferenceError('AUDIO_PREFERENCES_MICROPHONE_UNAVAILABLE');
      }
      try {
        candidateTrack.enabled = !this.muted;
      } catch {
        logger.warn('sparkone.browser_voice.audio_device_operation_failed', { failureCategory: 'input_device_mute_transfer_failed' });
        throw new AudioPreferenceError('AUDIO_PREFERENCES_MICROPHONE_SWITCH_FAILED');
      }

      const replacements: Array<{ sender: RTCRtpSender; previousTrack: MediaStreamTrack | null }> = [];
      try {
        for (const dialog of this.dialogs.values()) {
          for (const sender of dialog.rtc.connection?.getSenders() ?? []) {
            if (sender.track?.kind !== 'audio') continue;
            const previousTrack = sender.track;
            replacements.push({ sender, previousTrack });
            await sender.replaceTrack(candidateTrack);
          }
        }
      } catch {
        for (const { sender, previousTrack } of replacements.reverse()) {
          try { await sender.replaceTrack(previousTrack); } catch {
            logger.warn('sparkone.browser_voice.audio_device_operation_failed', { failureCategory: 'input_device_rollback_failed' });
          }
        }
        logger.warn('sparkone.browser_voice.audio_device_operation_failed', { failureCategory: 'input_device_replacement_failed' });
        throw new AudioPreferenceError('AUDIO_PREFERENCES_MICROPHONE_SWITCH_FAILED');
      }

      const previousStream = this.localStream;
      this.localStream = replacement;
      this.inputDeviceId = deviceId;
      replacement = null;
      stopStreamTracks(previousStream, 'input_device_previous_stream_stop_failed');
    } catch (error) {
      if (replacement) stopStreamTracks(replacement, 'input_device_candidate_cleanup_failed');
      if (error instanceof AudioPreferenceError) throw error;
      logger.warn('sparkone.browser_voice.audio_device_operation_failed', { failureCategory: 'input_device_acquisition_failed' });
      throw new AudioPreferenceError('AUDIO_PREFERENCES_MICROPHONE_SWITCH_FAILED');
    }
  }

  async setOutputDevice(deviceId: string): Promise<void> {
    if (!this.remoteAudio || !('setSinkId' in this.remoteAudio)) {
      throw new AudioPreferenceError('AUDIO_PREFERENCES_SPEAKER_UNSUPPORTED');
    }
    if (typeof deviceId !== 'string') {
      throw new AudioPreferenceError('AUDIO_PREFERENCES_SPEAKER_SWITCH_FAILED');
    }
    try {
      await (this.remoteAudio as HTMLAudioElement & { setSinkId(id: string): Promise<void> }).setSinkId(deviceId);
      this.outputDeviceId = deviceId;
    } catch {
      logger.warn('sparkone.browser_voice.audio_device_operation_failed', { failureCategory: 'output_device_selection_failed' });
      throw new AudioPreferenceError('AUDIO_PREFERENCES_SPEAKER_SWITCH_FAILED');
    }
  }

  async retryRemoteAudio(): Promise<void> {
    const dialogId = this.remoteAudioDialogId;
    const dialog = dialogId ? this.dialogs.get(dialogId) : null;
    if (!dialogId || !dialog?.remoteStream) {
      throw new Error('Browser call audio is not available yet.');
    }
    await this.startRemotePlayback(dialogId, dialog.remoteStream, 'playback-retry');
  }

  setMuted(muted: boolean): void {
    if (!this.isActive() || !this.localStream) {
      throw new Error('Browser microphone is not available for this call.');
    }
    const tracks = this.localStream
      .getAudioTracks()
      .filter((track) => track.readyState === 'live');
    if (tracks.length === 0) {
      throw new Error('Browser microphone is not available for this call.');
    }

    const previousStates = tracks.map((track) => track.enabled);
    try {
      tracks.forEach((track) => {
        track.enabled = !muted;
      });
    } catch {
      tracks.forEach((track, index) => {
        try {
          track.enabled = previousStates[index];
        } catch {
          logger.warn('sparkone.browser_voice.microphone_rollback_failed', { trackIndex: index });
        }
      });
      throw new Error('Could not update the browser microphone.');
    }
    this.muted = muted;
  }

  /**
   * Replaces only ICE/TURN configuration for future peer connections. Existing
   * dialogs keep their established transport and are never renegotiated or
   * terminated merely because short-lived relay credentials changed.
   */
  updateMediaCredentials(
    sessionId: string,
    iceServers: VoiceBrowserSession['iceServers'],
  ): boolean {
    let updated = false;
    for (const runtime of this.runtimes.values()) {
      if (runtime.sessionId !== sessionId) continue;
      runtime.session = { ...runtime.session, iceServers };
      updated = true;
    }
    return updated;
  }

  renewCredentials(session: VoiceBrowserSession): void {
    const predecessor = this.activeRuntime;
    if (!this.acceptingOffers || !predecessor || !this.jssip) {
      throw new Error('Browser registration authority is unavailable.');
    }
    if (this.pendingCredentialRotation) {
      if (this.pendingCredentialRotation.successorSessionId === session.sessionId) return;
      throw new Error('Browser credential rotation is already in progress.');
    }

    const successor = this.createRuntime(session, 'cutover_successor');
    this.pendingCredentialRotation = {
      predecessor,
      successor,
      successorSessionId: session.sessionId,
    };
    try {
      successor.ua.start();
    } catch (error) {
      this.pendingCredentialRotation = null;
      this.stopRuntime(successor, 'start_failed');
      this.emit(
        'failed',
        'Credential renewal failed; the existing registration is preserved.',
        undefined,
        'registration',
        session.sessionId,
      );
      throw error;
    }
  }

  confirmCredentialCutover(successorSessionId: string): void {
    const rotation = this.pendingCredentialRotation;
    if (!rotation || rotation.successorSessionId !== successorSessionId) return;
    if (rotation.successor.lifecycle !== 'registered') {
      logger.warn('sparkone.browser_voice.sip_cutover_confirmed_before_registration', {
        generation: rotation.successor.generation,
        lifecycle: rotation.successor.lifecycle,
      });
      return;
    }
    this.pendingCredentialRotation = null;
    rotation.successor.authority = 'active';
    rotation.predecessor.authority = 'cutover_predecessor';
    rotation.predecessor.lifecycle = 'retiring';
    this.activeRuntime = rotation.successor;
    this.ua = rotation.successor.ua;
    this.retireRuntime(rotation.predecessor);
  }

  cancelCredentialRotation(successorSessionId: string): void {
    const rotation = this.pendingCredentialRotation;
    if (!rotation || rotation.successorSessionId !== successorSessionId) return;
    this.pendingCredentialRotation = null;
    this.stopRuntime(rotation.successor, 'cutover_cancelled');
  }

  async start(session: VoiceBrowserSession): Promise<void> {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      throw new Error('SIP softphone requires a browser environment.');
    }
    if (this.activeRuntime) return;
    this.muted = false;

    try {
      this.jssip = await this.loadJsSip();
    } catch {
      this.emit('failed', 'Could not load the phone library.', undefined, 'signaling');
      throw new Error('Could not load the phone library.');
    }
    this.acceptingOffers = true;

    this.remoteAudio = document.createElement('audio');
    this.remoteAudio.autoplay = true;
    this.remoteAudio.setAttribute('playsinline', '');
    this.remoteAudio.hidden = true;
    document.body.appendChild(this.remoteAudio);

    // The runtime is fully constructed, callback-bound, and authoritative
    // before start() can cause a synchronous socket or UA callback.
    const runtime = this.createRuntime(session, 'active');
    this.activeRuntime = runtime;
    this.ua = runtime.ua;
    this.emit('registering', 'Connecting to the phone server…');
    try {
      runtime.ua.start();
    } catch (error) {
      this.activeRuntime = null;
      this.ua = null;
      this.stopRuntime(runtime, 'start_failed');
      this.dispose();
      this.emit('failed', 'Could not connect to the phone server.', undefined, 'signaling');
      throw error;
    }
  }

  /**
   * Revoke registration authority without interrupting established media.
   * Ringing offers and future INVITEs are stopped, while answering/connected
   * dialogs retain their owning UA until their normal terminal event.
   */
  suspendRegistrationAuthority(): void {
    this.acceptingOffers = false;
    for (const runtime of this.runtimes.values()) {
      runtime.authority = 'revoked';
      try {
        runtime.ua.unregister({ all: true });
      } catch {
        logger.warn('sparkone.browser_voice.sip_runtime_unregister_failed', {
          generation: runtime.generation,
          authority: runtime.authority,
        });
      }
    }
    for (const dialog of [...this.dialogs.values()]) {
      if (
        dialog.snapshot.state === 'answering' ||
        dialog.snapshot.state === 'connected'
      ) {
        continue;
      }
      this.terminateDialog(dialog.snapshot.dialogId);
    }
  }

  recoverRegistration(): void {
    const runtime = this.activeRuntime;
    if (!runtime || !this.acceptingOffers || runtime.authority !== 'active') return;
    this.scheduleRegistrationRecovery(runtime, true);
  }

  /**
   * Invokes exactly one authorized Browser-originated SIP INVITE. The opaque
   * intent exists only in this call stack and the JsSIP header; it is never
   * retained in dialog state, telemetry, cross-tab messages, or logs.
   */
  async originatePreparedCall(prepared: BrowserOriginatePreparedCall): Promise<SipDialogSnapshot> {
    const runtime = this.activeRuntime;
    if (
      !runtime ||
      !this.ua ||
      !this.acceptingOffers ||
      runtime.authority !== 'active' ||
      runtime.lifecycle !== 'registered'
    ) {
      throw new Error('Browser phone is not ready to start an outgoing call.');
    }
    if (this.outgoingInvocationInProgress || this.dialogs.size > 0) {
      throw new Error('A Browser call is already active.');
    }
    if (Date.parse(prepared.expiresAt) <= Date.now()) {
      throw new Error('Browser call authorization has expired.');
    }

    const invocation = {
      preparedCallId: prepared.preparedCallId,
      correlationId: prepared.correlationId,
      cancelled: false,
    };
    this.outgoingInvocationInProgress = true;
    this.pendingOutgoingInvocation = invocation;
    try {
      let localStream: MediaStream;
      try {
        localStream = await this.ensureMicrophone();
      } catch {
        if (invocation.cancelled) {
          throw new Error('Browser call was cancelled before it could start.');
        }
        this.emit('failed', 'Microphone access is required to start this call.', undefined, 'media');
        throw new Error('Microphone access was denied or unavailable.');
      }

      // getUserMedia cannot be force-cancelled, so claim cancellation after it
      // resolves and before JsSIP is allowed to create the SIP INVITE.
      if (invocation.cancelled || this.pendingOutgoingInvocation !== invocation) {
        this.releasePreparedManagedCallerMedia();
        throw new Error('Browser call was cancelled before it could start.');
      }

      let rtc: SipRtcSession;
      try {
        rtc = (runtime.ua as unknown as {
          call: (target: string, options: {
            mediaStream: MediaStream;
            pcConfig: RTCConfiguration;
            extraHeaders: readonly string[];
          }) => SipRtcSession;
        }).call(prepared.sipTarget, {
          mediaStream: localStream,
          pcConfig: { iceServers: toIceServers(runtime.session) },
          extraHeaders: [`${prepared.intentHeader}: ${prepared.intent}`],
        });
      } catch {
        this.emit('failed', 'Could not start the browser call.', undefined, 'signaling');
        throw new Error('Could not start the browser call.');
      }

      if (!rtc || rtc.direction !== 'outgoing') {
        try {
          rtc?.terminate();
        } catch {
          logger.warn('sparkone.browser_voice.sip_dialog_terminate_failed', { operation: 'invalid_outgoing_session' });
        }
        this.emit('failed', 'Could not start the browser call.', undefined, 'signaling');
        throw new Error('Phone library returned an invalid outgoing call session.');
      }

      this.managedCallerMediaPrepared = false;
      return this.addDialog(runtime.ua, runtime.session, rtc, prepared.correlationId);
    } finally {
      if (this.pendingOutgoingInvocation === invocation) {
        this.pendingOutgoingInvocation = null;
      }
      this.outgoingInvocationInProgress = false;
    }
  }

  cancelPreparedOutgoingCall(preparedCallId: string, correlationId: string): boolean {
    const invocation = this.pendingOutgoingInvocation;
    if (
      !invocation ||
      invocation.preparedCallId !== preparedCallId ||
      invocation.correlationId !== correlationId
    ) {
      return false;
    }
    invocation.cancelled = true;
    return true;
  }

  async answerDialog(dialogId: string): Promise<void> {
    const dialog = this.dialogs.get(dialogId);
    if (!dialog || dialog.snapshot.direction !== 'incoming') {
      throw new Error('Incoming browser call is no longer available.');
    }
    if (dialog.snapshot.state === 'connected' || dialog.snapshot.state === 'answering') return;
    if (dialog.snapshot.state !== 'ringing') {
      throw new Error('Incoming browser call is no longer available.');
    }
    dialog.snapshot = { ...dialog.snapshot, state: 'answering' };
    this.emitDiagnostic({ kind: 'auto_answer_started', sipCorrelationId: dialog.snapshot.sipCorrelationId });
    this.emit('answering', 'Answering browser call…', dialog.snapshot);
    try {
      const localStream = await this.ensureMicrophone();
      const current = this.dialogs.get(dialogId);
      if (!current || current.snapshot.state !== 'answering') return;
      dialog.rtc.answer({
        mediaStream: localStream,
        pcConfig: { iceServers: toIceServers(dialog.session) },
      });
      this.emitDiagnostic({ kind: 'answer_invoked', sipCorrelationId: current.snapshot.sipCorrelationId });
      this.managedCallerMediaPrepared = false;
    } catch {
      this.emitDiagnostic({ kind: 'auto_answer_failed', sipCorrelationId: dialog.snapshot.sipCorrelationId });
      const current = this.dialogs.get(dialogId);
      if (current) {
        current.snapshot = { ...current.snapshot, state: 'ringing' };
        this.emit(
          'incoming',
          'Microphone access is required to answer this call.',
          current.snapshot,
          'media',
        );
      }
      throw new Error('Microphone access was denied or unavailable.');
    }
  }

  rejectDialog(dialogId: string): void {
    const dialog = this.dialogs.get(dialogId);
    if (!dialog || dialog.snapshot.direction !== 'incoming') return;
    if (dialog.snapshot.state !== 'ringing' && dialog.snapshot.state !== 'answering') return;
    dialog.snapshot = { ...dialog.snapshot, state: 'rejected' };
    try {
      dialog.rtc.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
    } finally {
      this.finishDialog(dialogId, 'rejected', 'Call rejected.');
    }
  }

  terminateDialog(dialogId: string): void {
    const dialog = this.dialogs.get(dialogId);
    if (!dialog) return;
    try {
      dialog.rtc.terminate();
    } finally {
      this.finishDialog(dialogId, 'ended', 'Call ended.');
    }
  }

  hangup(): void {
    const wasActive = this.isActive();
    for (const dialog of this.dialogs.values()) {
      try {
        dialog.rtc.terminate();
      } catch {
        logger.warn('sparkone.browser_voice.sip_dialog_terminate_failed', { operation: 'hangup' });
      }
    }
    this.dispose();
    if (wasActive && this.currentStatus !== 'ended') {
      this.emit('ended', 'Browser phone stopped.');
    }
  }

  private createRuntime(session: VoiceBrowserSession, authority: SipUaAuthority): UaRuntime {
    if (!this.jssip) throw new Error('Phone library is not loaded.');
    const runtime = {} as UaRuntime;
    const socket = createInstrumentedSipSocket(
      new this.jssip.WebSocketInterface(session.signalingUrl),
      (event) => this.handleSocketObservation(runtime, event),
    );
    const ua = new this.jssip.UA(this.configuration(session, socket));
    Object.assign(runtime, {
      generation: ++this.nextRuntimeGeneration,
      sessionId: session.sessionId,
      session,
      ua,
      socket,
      authority,
      lifecycle: 'starting',
      inviteCallbackBound: false,
      inviteWatchdog: null,
      inviteObservedAt: null,
      retirementTimeout: null,
      registrationRecoveryTimeout: null,
      registrationRecoveryAttempts: 0,
    });
    this.runtimes.set(ua, runtime);
    this.bindUa(runtime, 'Connected to the phone server.');
    return runtime;
  }

  private bindUa(runtime: UaRuntime, registeredMessage: string): void {
    const { ua } = runtime;
    runtime.inviteCallbackBound = true;
    ua.on('registered', () => {
      if (!this.isRuntimeCurrent(runtime) || runtime.authority === 'revoked') return;
      this.clearRegistrationRecovery(runtime);
      runtime.lifecycle = 'registered';
      this.emitDiagnostic({ kind: 'registration_ready', registrationState: 'registered' });
      this.emit('registered', registeredMessage, undefined, undefined, runtime.session.sessionId);
    });
    ua.on('registrationFailed', (data: unknown) => {
      if (!this.isRuntimeCurrent(runtime)) return;
      const terminal = this.isTerminalRegistrationFailure(data);
      if (
        this.restoreRenewalPredecessor(
          runtime,
          terminal ? 'terminal' : 'retryable',
        )
      )
        return;
      if (runtime !== this.activeRuntime || runtime.authority !== 'active') return;
      if (terminal) {
        this.clearRegistrationRecovery(runtime);
        if (this.hasConnectedDialog()) {
          this.emit(
            'failed',
            'Browser registration credentials were rejected; the connected call is preserved.',
            undefined,
            'registration',
            runtime.session.sessionId,
            'terminal',
          );
          return;
        }
        this.dispose();
        this.emit(
          'failed',
          'Browser registration credentials were rejected.',
          undefined,
          'registration',
          runtime.session.sessionId,
          'terminal',
        );
        return;
      }
      runtime.lifecycle = 'degraded';
      this.emit(
        'failed',
        'Browser registration is temporarily unavailable. Reconnecting…',
        undefined,
        'registration',
        runtime.session.sessionId,
        'retryable',
      );
      if (!this.hasConnectedDialog()) this.scheduleRegistrationRecovery(runtime);
    });
    ua.on('newRTCSession', (data: NewRTCSessionData) => {
      if (!this.isRuntimeCurrent(runtime)) return;
      // Reaching JsSIP's public callback proves the transport-to-parser handoff
      // completed. Cancel the raw-INVITE watchdog before any application-level
      // authority decision so a deliberately rejected offer cannot later be
      // misclassified as a parser timeout and replace a healthy registration.
      this.cancelInviteWatchdog(runtime);
      this.emitSocketDiagnostic(runtime, 'invite_callback_invoked', { textOrBinary: 'text', byteLengthBucket: 0 });
      const dialogDirection = data.session.direction;
      // Browser-originated calls are created by originatePreparedCall(), which
      // owns the resulting local RTCSession. The UA event remains the inbound
      // offer authority and must never create a duplicate outgoing dialog.
      if (data.originator === 'local') return;
      if (!this.acceptingOffers || !this.isRuntimeEligibleForInvite(runtime)) {
        try {
          data.session.terminate({ status_code: 480, reason_phrase: 'Temporarily Unavailable' });
        } catch {
          logger.warn('sparkone.browser_voice.sip_offer_rejection_failed', { generation: runtime.generation, authority: runtime.authority });
        }
        this.emitDiagnostic({ kind: 'offer_rejected_authority', dialogDirection });
        return;
      }
      const sipCorrelationId = readCorrelation(data.request);
      const correlationState = correlationHeaderState(data.request);
      this.emitDiagnostic({ kind: 'offer_received', sipCorrelationId, dialogDirection });
      this.emitDiagnostic(correlationState === 'valid'
        ? { kind: 'correlation_present', sipCorrelationId: sipCorrelationId!, dialogDirection }
        : correlationState === 'malformed'
          ? { kind: 'correlation_malformed', dialogDirection }
          : { kind: 'correlation_missing', dialogDirection });
      this.addDialog(ua, runtime.session, data.session, sipCorrelationId);
    });
    ua.on('unregistered', () => {
      if (!this.isRuntimeCurrent(runtime)) return;
      if (runtime.lifecycle === 'retiring') {
        this.stopRetiredRuntimeIfUnused(runtime, 'unregistered');
        return;
      }
      if (this.restoreRenewalPredecessor(runtime)) return;
      if (runtime !== this.activeRuntime || runtime.authority !== 'active') return;
      runtime.lifecycle = 'degraded';
      this.emitDiagnostic({ kind: 'registration_disconnected', registrationState: 'disconnected' });
      this.emit(
        'failed',
        this.hasConnectedDialog()
          ? 'Browser registration disconnected; the connected call is preserved.'
          : 'Browser registration disconnected. Reconnecting…',
        undefined,
        'signaling',
        runtime.session.sessionId,
        'retryable',
      );
      if (!this.hasConnectedDialog()) this.scheduleRegistrationRecovery(runtime);
    });
    ua.on('disconnected', () => {
      if (!this.isRuntimeCurrent(runtime)) return;
      this.emitDiagnostic({ kind: 'registration_disconnected', registrationState: 'disconnected' });
      if (runtime.lifecycle === 'retiring') {
        this.stopRetiredRuntimeIfUnused(runtime, 'disconnected');
        return;
      }
      if (this.restoreRenewalPredecessor(runtime)) return;
      if (runtime !== this.activeRuntime || runtime.authority !== 'active') return;
      runtime.lifecycle = 'degraded';
      this.emit(
        'failed',
        this.hasConnectedDialog()
          ? 'Browser registration disconnected; the connected call is preserved.'
          : 'Browser registration disconnected. Reconnecting…',
        undefined,
        'signaling',
        runtime.session.sessionId,
        'retryable',
      );
      if (!this.hasConnectedDialog()) this.scheduleRegistrationRecovery(runtime);
    });
  }

  private isTerminalRegistrationFailure(data: unknown): boolean {
    const details =
      data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
    const response =
      details.response && typeof details.response === 'object'
        ? (details.response as Record<string, unknown>)
        : {};
    const rawStatus =
      response.status_code ??
      response.statusCode ??
      details.status_code ??
      details.statusCode;
    const status =
      typeof rawStatus === 'number' || typeof rawStatus === 'string'
        ? Number(rawStatus)
        : null;
    if (status === 401 || status === 403) return true;
    const cause =
      typeof details.cause === 'string' ? details.cause.toLowerCase() : '';
    return cause.includes('authentication') || cause.includes('authorization') || cause.includes('forbidden');
  }

  private scheduleRegistrationRecovery(runtime: UaRuntime, immediate = false): void {
    if (
      !this.isRuntimeCurrent(runtime) ||
      runtime !== this.activeRuntime ||
      runtime.authority !== 'active'
    )
      return;
    if (runtime.registrationRecoveryTimeout !== null) return;
    if (runtime.registrationRecoveryAttempts >= REGISTRATION_RECOVERY_MAX_ATTEMPTS) {
      this.emit(
        'failed',
        'Browser registration could not reconnect. Replacing the registration…',
        undefined,
        'registration',
        runtime.sessionId,
        'retryable',
        true,
      );
      return;
    }
    const attempt = ++runtime.registrationRecoveryAttempts;
    const delay = immediate
      ? 0
      : Math.min(
          8_000,
          REGISTRATION_RECOVERY_BASE_DELAY_MS * 2 ** (attempt - 1),
        );
    runtime.registrationRecoveryTimeout = setTimeout(() => {
      runtime.registrationRecoveryTimeout = null;
      if (
        !this.isRuntimeCurrent(runtime) ||
        runtime !== this.activeRuntime ||
        !this.acceptingOffers ||
        runtime.authority !== 'active'
      )
        return;
      this.emit('registering', 'Reconnecting to the phone server…', undefined, undefined, runtime.sessionId);
      try {
        runtime.ua.register();
      } catch {
        this.scheduleRegistrationRecovery(runtime);
      }
    }, delay);
  }

  private clearRegistrationRecovery(runtime: UaRuntime): void {
    if (runtime.registrationRecoveryTimeout !== null) clearTimeout(runtime.registrationRecoveryTimeout);
    runtime.registrationRecoveryTimeout = null;
    runtime.registrationRecoveryAttempts = 0;
  }

  private isRuntimeCurrent(runtime: UaRuntime): boolean {
    return this.runtimes.get(runtime.ua) === runtime && runtime.lifecycle !== 'stopped';
  }

  private isRuntimeEligibleForInvite(runtime: UaRuntime): boolean {
    return this.isRuntimeCurrent(runtime)
      && runtime.inviteCallbackBound
      && (runtime.authority === 'active' || runtime.authority === 'cutover_predecessor' || runtime.authority === 'cutover_successor')
      && runtime.lifecycle !== 'degraded';
  }

  private handleSocketObservation(runtime: UaRuntime, observation: SipSocketObservation): void {
    if (!this.isRuntimeEligibleForInvite(runtime)) return;
    if (observation.kind === 'response_sent') {
      this.handleInviteRejectionBeforeCallback(runtime, observation.responseStatus);
      return;
    }
    if (observation.kind === 'message_received') {
      this.emitSocketDiagnostic(runtime, 'transport_message_received', observation);
      return;
    }
    if (runtime.inviteWatchdog !== null) return;
    runtime.inviteObservedAt = Date.now();
    this.emitSocketDiagnostic(runtime, 'transport_invite_observed', observation);
    runtime.inviteWatchdog = setTimeout(() => {
      if (!this.isRuntimeEligibleForInvite(runtime) || runtime.inviteWatchdog === null) return;
      const elapsedMs = Math.min(30_000, Math.max(0, Date.now() - (runtime.inviteObservedAt ?? Date.now())));
      runtime.inviteWatchdog = null;
      runtime.inviteObservedAt = null;
      runtime.lifecycle = 'degraded';
      this.emitDiagnostic({ kind: 'invite_callback_timeout', generation: runtime.generation, authority: runtime.authority, lifecycle: runtime.lifecycle, callbackBound: runtime.inviteCallbackBound, elapsedMs });
      this.emit('failed', 'Browser SIP delivery could not create a call session.', undefined, 'signaling');
      this.recoverDegradedRuntime(runtime);
    }, INVITE_CALLBACK_WATCHDOG_MS);
  }

  private handleInviteRejectionBeforeCallback(runtime: UaRuntime, responseStatus: SipPreCallbackRejectionStatus): void {
    if (runtime.inviteWatchdog === null) return;
    this.cancelInviteWatchdog(runtime);
    // A sent SIP rejection proves this UA handled the transaction. It must not
    // be treated as registration degradation or trigger a replacement UA.
    this.emitDiagnostic({ kind: 'invite_rejected_before_callback', generation: runtime.generation, authority: runtime.authority, lifecycle: runtime.lifecycle, callbackBound: runtime.inviteCallbackBound, responseStatus });
  }

  private emitSocketDiagnostic(
    runtime: UaRuntime,
    kind: Extract<SipSoftphoneDiagnosticEvent['kind'], 'transport_message_received' | 'transport_invite_observed' | 'invite_callback_invoked'>,
    observation: {
      textOrBinary: SipSocketPayloadKind;
      byteLengthBucket: number;
    },
  ): void {
    this.emitDiagnostic({ kind, generation: runtime.generation, authority: runtime.authority, lifecycle: runtime.lifecycle, callbackBound: runtime.inviteCallbackBound, textOrBinary: observation.textOrBinary, byteLengthBucket: observation.byteLengthBucket });
  }

  private cancelInviteWatchdog(runtime: UaRuntime): void {
    if (runtime.inviteWatchdog !== null) clearTimeout(runtime.inviteWatchdog);
    runtime.inviteWatchdog = null;
    runtime.inviteObservedAt = null;
  }

  private recoverDegradedRuntime(
    runtime: UaRuntime,
    reason: 'invite_callback_timeout' | 'invite_rejected_before_callback' = 'invite_callback_timeout',
  ): void {
    if (runtime !== this.activeRuntime || !this.acceptingOffers || this.hasConnectedDialog()) return;
    runtime.authority = 'revoked';
    this.stopRuntime(runtime, reason);
    this.activeRuntime = null;
    this.ua = null;
    let replacement: UaRuntime | null = null;
    try {
      replacement = this.createRuntime(runtime.session, 'active');
      this.activeRuntime = replacement;
      this.ua = replacement.ua;
      replacement.ua.start();
      this.emit('registering', 'Reconnecting to the phone server…');
    } catch {
      // A replacement can be constructed and made authoritative before its
      // transport start throws. Roll it back synchronously so no stopped UA
      // remains advertised as dispatch-ready or can later emit registration.
      if (replacement) {
        if (this.activeRuntime === replacement) this.activeRuntime = null;
        if (this.ua === replacement.ua) this.ua = null;
        this.stopRuntime(replacement, 'start_failed');
      } else {
        this.activeRuntime = null;
        this.ua = null;
      }
      logger.warn('sparkone.browser_voice.sip_runtime_recovery_failed', {
        failedGeneration: runtime.generation,
      });
    }
  }

  private retireRuntime(runtime: UaRuntime): void {
    if (!this.isRuntimeCurrent(runtime)) return;
    try {
      runtime.ua.unregister({ all: true });
    } catch {
      logger.warn('sparkone.browser_voice.sip_runtime_unregister_failed', { generation: runtime.generation, authority: runtime.authority });
    }
    // An answering or connected predecessor owns live media. It must remain
    // available until its final dialog terminates, even after JsSIP confirms
    // the registrar unregistration. Idle runtimes retain the bounded fallback
    // stop in case that callback never arrives.
    // JsSIP may emit unregistered synchronously from unregister(). In that
    // case the idle runtime has already been stopped by its callback.
    if (
      this.isRuntimeCurrent(runtime) &&
      runtime.lifecycle === 'retiring' &&
      !this.runtimeOwnsDialog(runtime)
    ) {
      runtime.retirementTimeout = setTimeout(
        () => this.stopRuntime(runtime, 'retirement_timeout'),
        CREDENTIAL_CUTOVER_RETIREMENT_TIMEOUT_MS,
      );
    }
  }

  private runtimeOwnsDialog(runtime: UaRuntime): boolean {
    return Array.from(this.dialogs.values()).some(
      (dialog) => dialog.ownerUa === runtime.ua,
    );
  }

  private stopRetiredRuntimeIfUnused(
    runtime: UaRuntime,
    reason: 'unregistered' | 'disconnected',
  ): void {
    if (!this.runtimeOwnsDialog(runtime)) this.stopRuntime(runtime, reason);
  }

  private stopRuntime(runtime: UaRuntime, reason: 'start_failed' | 'cutover_cancelled' | 'unregistered' | 'disconnected' | 'invite_callback_timeout' | 'invite_rejected_before_callback' | 'retirement_timeout'): void {
    if (!this.isRuntimeCurrent(runtime)) return;
    this.cancelInviteWatchdog(runtime);
    this.clearRegistrationRecovery(runtime);
    if (runtime.retirementTimeout !== null) clearTimeout(runtime.retirementTimeout);
    runtime.retirementTimeout = null;
    runtime.lifecycle = 'stopped';
    runtime.authority = 'revoked';
    this.runtimes.delete(runtime.ua);
    try {
      runtime.ua.stop();
    } catch {
      logger.warn('sparkone.browser_voice.sip_runtime_stop_failed', { generation: runtime.generation, reason });
    }
  }

  private restoreRenewalPredecessor(
    failedSuccessor: UaRuntime,
    registrationFailure: 'retryable' | 'terminal' = 'retryable',
  ): boolean {
    const rotation = this.pendingCredentialRotation;
    if (!rotation || rotation.successor !== failedSuccessor || !this.isRuntimeCurrent(rotation.predecessor)) return false;
    this.pendingCredentialRotation = null;
    this.stopRuntime(failedSuccessor, 'disconnected');
    rotation.predecessor.authority = 'active';
    rotation.predecessor.lifecycle = 'registered';
    this.activeRuntime = rotation.predecessor;
    this.ua = rotation.predecessor.ua;
    this.emit(
      'failed',
      'Credential renewal failed; the existing registration is preserved.',
      undefined,
      'registration',
      failedSuccessor.sessionId,
      registrationFailure,
    );
    return true;
  }

  private async ensureMicrophone(): Promise<MediaStream> {
    if (this.localStream?.getAudioTracks().some((track) => track.readyState === 'live')) {
      return this.localStream;
    }
    this.emit('requesting-mic', 'Requesting microphone access…');
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: this.inputDeviceId
        ? { deviceId: { exact: this.inputDeviceId } }
        : true,
      video: false,
    });
    return this.localStream;
  }

  private addDialog(
    ua: UA,
    session: VoiceBrowserSession,
    rtc: SipRtcSession,
    sipCorrelationId: string | null,
  ): SipDialogSnapshot {
    const dialogId = createDialogId();
    const snapshot: SipDialogSnapshot = {
      dialogId,
      direction: rtc.direction,
      state: rtc.direction === 'incoming' ? 'ringing' : 'dialing',
      sipCorrelationId,
    };
    const entry: DialogEntry = {
      snapshot,
      rtc,
      ownerUa: ua,
      session,
      remoteTrackBound: false,
      remoteTrackObserved: false,
      peerconnection: null,
      inboundRtpBaseline: null,
      inboundRtpBaselineCaptured: false,
      inboundRtpBaselinePromise: null,
      inboundRtpClassified: false,
      mediaState: 'idle',
      mediaStartedAt: null,
      mediaTimeout: null,
      remoteStream: null,
      mediaAttempt: 0,
    };
    this.dialogs.set(dialogId, entry);

    rtc.on('progress', () => {
      const current = this.dialogs.get(dialogId);
      if (!current || current.snapshot.direction !== 'outgoing' || current.snapshot.state !== 'dialing') return;
      current.snapshot = { ...current.snapshot, state: 'ringing' };
      this.emit('outgoing_ringing', 'Browser call is ringing…', current.snapshot);
    });
    rtc.on('confirmed', () => {
      const current = this.dialogs.get(dialogId);
      if (!current) return;
      current.snapshot = { ...current.snapshot, state: 'connected' };
      this.emitDiagnostic({ kind: 'session_confirmed', sipCorrelationId: current.snapshot.sipCorrelationId });
      this.emit('connected', 'Call connected.', current.snapshot);
      if (current.mediaState !== 'ready') {
        this.beginMediaReadinessWait(dialogId);
      }
    });
    rtc.on('ended', () => {
      const current = this.dialogs.get(dialogId);
      this.emitDiagnostic({ kind: 'session_ended', sipCorrelationId: current?.snapshot.sipCorrelationId ?? null });
      this.finishDialog(dialogId, 'ended', 'Call ended.');
    });
    rtc.on('failed', () => {
      const current = this.dialogs.get(dialogId);
      // JsSIP may synchronously emit `failed` from terminate() while a local
      // Busy Here decline is still unwinding. The explicit rejected snapshot
      // is authoritative for that path; treating it as a signaling failure
      // leaves the persistent Browser phone in a false failed lifecycle.
      if (current?.snapshot.state === 'rejected') return;
      this.emitDiagnostic({ kind: 'session_failed', sipCorrelationId: current?.snapshot.sipCorrelationId ?? null });
      this.finishDialog(dialogId, 'failed', 'Call failed.', 'signaling');
    });
    rtc.on('peerconnection', ({ peerconnection }) => {
      this.bindRemoteAudioTracks(dialogId, peerconnection);
    });
    // JsSIP can create its RTCPeerConnection synchronously while call() is
    // executing, before it returns the RTCSession that reaches addDialog().
    // Bind the existing connection and inspect receivers as well as future
    // track events so that race cannot discard the remote audio track.
    if (rtc.connection) this.bindRemoteAudioTracks(dialogId, rtc.connection);

    this.emit(
      rtc.direction === 'incoming' ? 'incoming' : 'dialing',
      rtc.direction === 'incoming' ? 'Incoming browser call.' : 'Starting browser call…',
      snapshot,
    );
    return { ...snapshot };
  }

  private bindRemoteAudioTracks(dialogId: string, peerconnection: RTCPeerConnection): void {
    const dialog = this.dialogs.get(dialogId);
    if (!dialog || dialog.remoteTrackBound) return;
    dialog.peerconnection = peerconnection;
    try {
      peerconnection.addEventListener('track', (event) => {
        this.attachRemoteAudioTrack(dialogId, event.track, event.streams);
      });
      dialog.remoteTrackBound = true;
    } catch {
      logger.warn('sparkone.browser_voice.remote_audio_binding_failed', {
        operation: 'bind_remote_audio_track_listener',
      });
      this.failDialog(dialogId, 'Could not initialize browser call audio.');
      return;
    }
    try {
      // A peer connection created before addDialog() may have already emitted
      // its track event. Receivers retain that track, so attach it explicitly.
      const receivers = typeof peerconnection.getReceivers === 'function'
        ? peerconnection.getReceivers()
        : [];
      for (const receiver of receivers) {
        this.attachRemoteAudioTrack(dialogId, receiver.track, []);
      }
    } catch {
      // Receiver enumeration only recovers an already-fired track event. The
      // listener above remains sufficient for all future track delivery.
      logger.warn('sparkone.browser_voice.remote_audio_binding_failed', {
        operation: 'reconcile_remote_receivers',
      });
    }
  }

  private attachRemoteAudioTrack(
    dialogId: string,
    track: MediaStreamTrack | undefined,
    streams: readonly MediaStream[],
  ): void {
    const dialog = this.dialogs.get(dialogId);
    if (!dialog || !this.remoteAudio || (track && track.kind !== 'audio')) return;
    this.observeRemoteAudioTrack(dialogId, dialog);
    const stream = streams[0] ?? dialog.remoteStream ?? (
      track?.kind === 'audio' ? new MediaStream() : null
    );
    if (!stream) return;
    if (track?.kind === 'audio' && !stream.getTracks().some((candidate) => candidate === track)) {
      stream.addTrack(track);
    }
    void this.startRemotePlayback(dialogId, stream, 'track-received').catch(() => {
      logger.warn('sparkone.browser_voice.remote_playback_start_failed', { stage: 'track-received' });
    });
  }

  private observeRemoteAudioTrack(dialogId: string, dialog: DialogEntry): void {
    if (dialog.remoteTrackObserved) return;
    dialog.remoteTrackObserved = true;
    logger.info('sparkone.browser_voice.remote_track_observed', {});
    const baselinePromise = this.captureInboundRtpBaseline(dialogId);
    dialog.inboundRtpBaselinePromise = baselinePromise;
    void baselinePromise.finally(() => {
      if (this.dialogs.get(dialogId) === dialog && dialog.inboundRtpBaselinePromise === baselinePromise) {
        dialog.inboundRtpBaselinePromise = null;
      }
    });
  }

  private async captureInboundRtpBaseline(dialogId: string): Promise<void> {
    const dialog = this.dialogs.get(dialogId);
    if (!dialog || dialog.inboundRtpBaselineCaptured) return;
    dialog.inboundRtpBaselineCaptured = true;
    const baseline = await this.readInboundAudioRtpPackets(dialog.peerconnection);
    if (this.dialogs.get(dialogId) === dialog) dialog.inboundRtpBaseline = baseline;
  }

  private async classifyInboundRtpAtTimeout(dialogId: string): Promise<void> {
    const dialog = this.dialogs.get(dialogId);
    if (!dialog || dialog.inboundRtpClassified) return;
    dialog.inboundRtpClassified = true;
    // The track-triggered baseline and timeout sample must be ordered; without
    // this await, a slow baseline can overwrite the comparison state.
    await dialog.inboundRtpBaselinePromise;
    if (this.dialogs.get(dialogId) !== dialog) return;
    const currentPackets = await this.readInboundAudioRtpPackets(dialog.peerconnection);
    if (this.dialogs.get(dialogId) !== dialog) return;
    const state: InboundRtpState = dialog.inboundRtpBaselineCaptured
      && dialog.inboundRtpBaseline !== null
      && currentPackets !== null
      ? currentPackets > dialog.inboundRtpBaseline ? 'advancing' : 'none'
      : 'unavailable';
    logger.info('sparkone.browser_voice.inbound_rtp_classified', { state });
  }

  private async readInboundAudioRtpPackets(peerconnection: RTCPeerConnection | null): Promise<number | null> {
    if (!peerconnection || typeof peerconnection.getStats !== 'function') return null;
    try {
      const reports = await peerconnection.getStats();
      let packetCount = 0;
      let audioReportFound = false;
      for (const report of reports.values()) {
        if (
          report.type !== 'inbound-rtp'
          || (report.kind !== 'audio' && report.mediaType !== 'audio')
          || typeof report.packetsReceived !== 'number'
          || !Number.isFinite(report.packetsReceived)
          || report.packetsReceived < 0
        ) continue;
        audioReportFound = true;
        packetCount += report.packetsReceived;
      }
      return audioReportFound ? packetCount : null;
    } catch {
      // Stats are diagnostic only. They must never affect active media.
      return null;
    }
  }

  private failDialog(dialogId: string, message: string): void {
    const dialog = this.dialogs.get(dialogId);
    if (!dialog) return;
    try {
      dialog.rtc.terminate();
    } catch {
      logger.warn('sparkone.browser_voice.sip_dialog_terminate_failed', { operation: 'media_failure' });
    }
    this.finishDialog(dialogId, 'failed', message, 'media');
  }

  private beginMediaReadinessWait(dialogId: string): void {
    const dialog = this.dialogs.get(dialogId);
    if (!dialog || dialog.mediaState === 'ready') return;
    dialog.mediaStartedAt ??= Date.now();
    if (dialog.mediaState !== 'connecting') {
      dialog.mediaState = 'connecting';
      this.emitMedia(
        dialog,
        'connecting',
        'signaling-confirmed',
        'Connecting browser audio…',
      );
    }
    if (dialog.mediaTimeout !== null) return;
    dialog.mediaTimeout = setTimeout(() => {
      const current = this.dialogs.get(dialogId);
      if (!current || current.mediaState === 'ready') return;
      current.mediaTimeout = null;
      current.mediaState = 'delayed';
      logger.warn('sparkone.browser_voice.remote_playback_timeout', {});
      void this.classifyInboundRtpAtTimeout(dialogId);
      this.emitMedia(
        current,
        'delayed',
        'playback-timeout',
        'Browser audio is taking longer than expected. You can retry playback.',
      );
    }, mediaReadinessTimeoutMs);
  }

  private async startRemotePlayback(
    dialogId: string,
    stream: MediaStream,
    stage: Extract<SipMediaStage, 'track-received' | 'playback-retry'>,
  ): Promise<void> {
    const dialog = this.dialogs.get(dialogId);
    const audio = this.remoteAudio;
    if (!dialog || !audio) return;
    dialog.remoteStream = stream;
    dialog.mediaStartedAt ??= Date.now();
    dialog.mediaState = 'connecting';
    const attempt = ++dialog.mediaAttempt;
    this.remoteAudioDialogId = dialogId;
    audio.srcObject = stream;
    this.emitMedia(dialog, 'connecting', stage, 'Connecting browser audio…');
    this.beginMediaReadinessWait(dialogId);
    let resolvePlaying: (() => void) | null = null;
    const playing = new Promise<void>((resolve) => {
      resolvePlaying = resolve;
    });
    const onPlaying = () => resolvePlaying?.();
    audio.addEventListener('playing', onPlaying, { once: true });
    try {
      await audio.play();
      if (audio.paused || audio.readyState < audio.HAVE_FUTURE_DATA) {
        await playing;
      }
    } catch {
      this.emitDiagnostic({ kind: 'remote_playback_blocked', sipCorrelationId: dialog.snapshot.sipCorrelationId });
      const current = this.dialogs.get(dialogId);
      if (
        !current ||
        current.mediaAttempt !== attempt ||
        this.remoteAudioDialogId !== dialogId ||
        audio.srcObject !== stream
      )
        return;
      this.clearMediaTimeout(current);
      current.mediaState = 'blocked';
      logger.warn('sparkone.browser_voice.remote_playback_blocked', {});
      this.emitMedia(
        current,
        'blocked',
        'playback-blocked',
        'Browser audio needs permission to play. Select Enable audio to continue.',
      );
      throw new Error('Browser audio playback was blocked.');
    } finally {
      audio.removeEventListener('playing', onPlaying);
    }
    const current = this.dialogs.get(dialogId);
    if (
      !current ||
      current.mediaAttempt !== attempt ||
      this.remoteAudioDialogId !== dialogId ||
      audio.srcObject !== stream
    )
      return;
    this.clearMediaTimeout(current);
    current.mediaState = 'ready';
    logger.info('sparkone.browser_voice.remote_playback_ready', {});
    this.emitMedia(current, 'ready', 'playback-ready', 'Browser audio connected.');
  }

  private clearMediaTimeout(dialog: DialogEntry): void {
    if (dialog.mediaTimeout !== null) clearTimeout(dialog.mediaTimeout);
    dialog.mediaTimeout = null;
  }

  private emitMedia(
    dialog: DialogEntry,
    mediaState: SipMediaState,
    mediaStage: SipMediaStage,
    message: string,
  ): void {
    this.onChange?.({
      status: this.currentStatus,
      message,
      dialog: { ...dialog.snapshot },
      mediaState,
      mediaStage,
      ...(dialog.mediaStartedAt === null
        ? {}
        : { mediaElapsedMs: Math.min(300_000, Math.max(0, Date.now() - dialog.mediaStartedAt)) }),
    });
  }

  private finishDialog(
    dialogId: string,
    state: Extract<SipDialogState, 'rejected' | 'ended' | 'failed'>,
    message: string,
    failureCategory?: SipFailureCategory,
  ): void {
    const dialog = this.dialogs.get(dialogId);
    if (!dialog) return;
    const snapshot = { ...dialog.snapshot, state };
    this.clearMediaTimeout(dialog);
    this.dialogs.delete(dialogId);
    if (this.remoteAudio && this.remoteAudioDialogId === dialogId) {
      this.remoteAudio.srcObject = null;
      this.remoteAudioDialogId = null;
    }
    this.stopUnusedRetiredUas();
    if (
      (state === 'ended' || state === 'failed') &&
      !this.managedCallerMediaPrepared &&
      !this.hasConnectedDialog()
    ) {
      this.releasePreparedManagedCallerMedia();
    }
    this.emit(state, message, snapshot, failureCategory);
    if (this.ua && this.acceptingOffers && this.dialogs.size === 0) {
      this.emit('registered', 'Browser phone ready.');
    }
  }

  private hasConnectedDialog(): boolean {
    return Array.from(this.dialogs.values()).some(
      ({ snapshot }) => snapshot.state === 'connected' || snapshot.state === 'answering',
    );
  }

  private stopUnusedRetiredUas(): void {
    for (const runtime of [...this.runtimes.values()]) {
      if (runtime.lifecycle !== 'retiring') continue;
      // A retired predecessor stops as soon as its final dialog ends. The
      // unregister callback and fallback timer only handle idle runtimes.
      this.stopRetiredRuntimeIfUnused(runtime, 'unregistered');
    }
  }

  private configuration(session: VoiceBrowserSession, socket: InstrumentedSipSocket): UaConfiguration {
    return {
      uri: session.sipUri,
      // Keep the registered Contact user stable across HMR, overlapping tabs,
      // reconnect recovery, and credential-cutover UAs. JsSIP otherwise creates
      // a random Contact user per runtime; Asterisk can then route an INVITE for
      // one runtime to another live UA sharing the same endpoint credentials,
      // where JsSIP rejects it before newRTCSession because the Request-URI does
      // not point to that UA. The Spark VP-issued SIP URI is already unique to
      // the Browser endpoint and is the correct registrar/contact authority.
      contact_uri: session.sipUri,
      password: session.sipSecret,
      sockets: [socket as unknown as WebSocketInterface],
      display_name: session.sipUsername,
      authorization_user: session.sipUsername,
      register: true,
      ...(session.registrationTtlSeconds > 0
        ? {
            register_expires: Math.min(
              3600,
              Math.max(60, session.registrationTtlSeconds),
            ),
          }
        : {}),
    };
  }

  private dispose(): void {
    for (const dialog of this.dialogs.values()) this.clearMediaTimeout(dialog);
    this.dialogs.clear();
    this.acceptingOffers = false;
    this.pendingCredentialRotation = null;
    for (const runtime of [...this.runtimes.values()]) {
      this.stopRuntime(runtime, 'disconnected');
    }
    this.activeRuntime = null;
    this.ua = null;
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = null;
    this.managedCallerMediaPrepared = false;
    this.outgoingInvocationInProgress = false;
    this.pendingOutgoingInvocation = null;
    this.muted = false;
    if (this.remoteAudio) {
      this.remoteAudio.srcObject = null;
      this.remoteAudio.remove();
      this.remoteAudio = null;
    }
    this.remoteAudioDialogId = null;
    this.jssip = null;
  }

  private emitDiagnostic(event: SipSoftphoneDiagnosticEvent): void {
    this.onDiagnostic?.(event);
  }

  private emit(
    status: SipSoftphoneStatus,
    message?: string,
    dialog?: SipDialogSnapshot,
    failureCategory?: SipFailureCategory,
    registrationSessionId?: string,
    registrationFailure?: 'retryable' | 'terminal',
    registrationRecoveryExhausted?: boolean,
  ): void {
    this.currentStatus = status;
    this.onChange?.({
      status,
      message,
      ...(dialog ? { dialog: { ...dialog } } : {}),
      ...(failureCategory ? { failureCategory } : {}),
      ...(registrationSessionId ? { registrationSessionId } : {}),
      ...(registrationFailure ? { registrationFailure } : {}),
      ...(registrationRecoveryExhausted
        ? { registrationRecoveryExhausted: true }
        : {}),
    });
  }
}
